'use client';

import type { ReactElement, ReactNode, Ref } from 'react';
import { useId, useState } from 'react';
import { ChevronDown } from 'react-bootstrap-icons';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';

import {
  CONTACT_PHONE_COUNTRIES,
  type ContactPhoneCountry,
  contactPhoneCountry,
  contactPhoneFromNational,
  contactPhoneNationalInput,
  normalizeContactPhoneToE164,
  normalizeUsPhoneToE164,
} from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSMenu, SDSMenuItem } from '@repo/sds/SDSMenu';
import { SDSText, type SDSTextType } from '@repo/sds/SDSText';
import { SDSTextInput } from '@repo/sds/SDSTextInput';

import { formatUsPhoneForDisplay } from './us-phone-input';

import './sms-phone-field.scss';

const ids = DataTestIds.SMS_MARKETING;
const FLAG_STYLE = { borderRadius: '2px', height: '0.9375rem', width: '1.25rem' };

interface SmsPhoneFieldProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  hint?: string;
  errorMessage?: string;
  isDisabled?: boolean;
  /** Shows a number that is already on the account, which only an action next to the label changes. */
  isReadOnly?: boolean;
  rightIcon?: ReactElement;
  /** A link-style action drawn opposite the label, such as changing the number. */
  labelAction?: ReactNode;
  /** Matches the labels of the fields around it; left out, it is the plain text input label. */
  labelType?: SDSTextType;
  inputRef?: Ref<HTMLInputElement>;
  allowInternational?: boolean;
  /** Overrides the shared phone input test id where a page needs its own. */
  dataTestId?: string;
  errorMessageTestId?: string;
}

/** Uses the seven verification destinations for accounts; marketing-only capture stays US-only. */
export function SmsPhoneField({
  value,
  onChange,
  onBlur,
  hint,
  errorMessage,
  isDisabled,
  isReadOnly,
  rightIcon,
  labelAction,
  labelType,
  inputRef,
  allowInternational = false,
  dataTestId = ids.PHONE_INPUT,
  errorMessageTestId,
}: SmsPhoneFieldProps) {
  const { t } = useTranslation('common');
  const inputId = useId();
  const hintId = useId();
  const label = t('smsMarketing.phoneField.label');
  const [country, setCountry] = useState<ContactPhoneCountry>('US');
  const activeCountry = (allowInternational && contactPhoneCountry(value)) || country;
  const selected = CONTACT_PHONE_COUNTRIES.find(item => item.code === activeCountry) ?? CONTACT_PHONE_COUNTRIES[0];
  const normalizer = allowInternational ? normalizeContactPhoneToE164 : normalizeUsPhoneToE164;

  function leave(): void {
    // Formatting waits for the field to lose focus, so typing and pasting never move the caret or drop digits.
    const phoneE164 = normalizer(value);
    if (phoneE164) {
      onChange(formatUsPhoneForDisplay(phoneE164));
    }
    onBlur?.();
  }

  return (
    // The same label style and label-to-input gap as SDSTextInput, so the field lines up with inputs beside it.
    <SDSFlexbox direction="column" gap="text-small" fullWidth>
      {/* The hint sits between the label and the field, so the label is drawn here rather than by the input. */}
      <div>
        <SDSFlexbox direction="row" justifyContent="space-between" alignItems="center" fullWidth>
          <label htmlFor={inputId} className="SDSInputLabel SmsPhoneField__label">
            {labelType ? <SDSText type={labelType} isInline text={label} /> : label}
          </label>
          {labelAction}
        </SDSFlexbox>
        {hint && <SDSText id={hintId} type="body-medium" color="muted" text={hint} />}
      </div>
      <SDSTextInput
        id={inputId}
        ref={inputRef}
        ariaLabel={label}
        ariaDescribedBy={hint ? hintId : undefined}
        placeholder={t('smsMarketing.phonePlaceholder')}
        name="phone"
        autoComplete={allowInternational ? 'tel-national' : 'tel'}
        inputMode="tel"
        textType={labelType === 'body-large' ? 'body-extra-large' : 'body-medium'}
        value={allowInternational && !isReadOnly ? contactPhoneNationalInput(value, activeCountry) : value}
        isFullWidth
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        errorMessage={errorMessage}
        errorMessageTestId={errorMessageTestId}
        leftIcon={
          allowInternational && !isReadOnly ? (
            <SDSMenu
              trigger={
                <SDSFlexbox gap="text-medium" alignItems="center">
                  <ReactCountryFlag countryCode={activeCountry} svg aria-label={selected.name} style={FLAG_STYLE} />
                  <SDSText type="button-medium" color="text-primary" isInline text={`+${selected.callingCode}`} />
                  <ChevronDown size={12} aria-hidden="true" />
                </SDSFlexbox>
              }
              triggerClassName="SmsPhoneField__country"
              triggerProps={{
                type: 'button',
                'aria-label': t('smsMarketing.phoneCountry'),
                disabled: isDisabled,
                'data-testid': ids.PHONE_COUNTRY,
              }}
              dataTestId={ids.PHONE_COUNTRY_MENU}
            >
              {CONTACT_PHONE_COUNTRIES.map(item => (
                <SDSMenuItem
                  key={item.code}
                  dataTestId={ids.PHONE_COUNTRY_OPTION}
                  onClick={() => {
                    setCountry(item.code);
                    onChange('');
                  }}
                >
                  {item.name} (+{item.callingCode})
                </SDSMenuItem>
              ))}
            </SDSMenu>
          ) : (
            <ReactCountryFlag
              countryCode={allowInternational ? activeCountry : 'US'}
              svg
              aria-label={allowInternational ? selected.name : t('smsMarketing.phoneField.country')}
              style={FLAG_STYLE}
            />
          )
        }
        prefix={(allowInternational && !isReadOnly) || (isReadOnly && value.startsWith('+')) ? undefined : '+1'}
        rightIcon={rightIcon}
        inputClassName={labelType === 'body-large' ? 'SmsPhoneField SmsPhoneField--large' : 'SmsPhoneField'}
        onChange={next => {
          if (allowInternational) {
            const detected = contactPhoneCountry(next);
            if (detected) setCountry(detected);
            onChange(contactPhoneFromNational(next, activeCountry));
          } else onChange(next);
        }}
        onBlur={leave}
        dataTestId={dataTestId}
      />
    </SDSFlexbox>
  );
}
